package kz.dentalcrm.app.screens.quick_replies.list

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.Plus
import compose.icons.feathericons.Search
import compose.icons.feathericons.X
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.empty_quick_replies
import dentalcrm.composeapp.generated.resources.quick_replies
import dentalcrm.composeapp.generated.resources.search
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.UIEmptyListState
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.core.ui.components.lazy_column.PaginatedLazyColumn
import kz.dentalcrm.app.core.ui.components.refresh.PullToRefresh
import kz.dentalcrm.app.core.ui.components.refresh.rememberPullToRefreshState
import kz.dentalcrm.app.data.network.response_model.quick_replies.QuickReply
import kz.dentalcrm.app.screens.quick_replies.add.AddQuickReplyDialog
import kz.dentalcrm.app.screens.quick_replies.edit.EditQuickReplyDialog
import kz.dentalcrm.app.screens.quick_replies.list.mvi.QuickRepliesViewEvent
import kz.dentalcrm.app.screens.quick_replies.list.mvi.QuickRepliesViewState
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.screens.ui.Icons.chevronRight
import kz.dentalcrm.app.theme.DefaultTheme
import kz.dentalcrm.app.theme.shimmer.ShimmerBounds
import kz.dentalcrm.app.theme.shimmer.rememberShimmer
import kz.dentalcrm.app.theme.shimmer.shimmer
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.screens.protocols.detail.DetailProtocolEvent

@Composable
fun QuickRepliesScreen() {
    val navigator: AppNavigator = koinInject()
    val viewModel: QuickRepliesViewModel = koinViewModel()
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        val state by viewModel.viewState.collectAsState()

        UiStateScaffold(
            modifier = Modifier.fillMaxSize(),
            content = {
                QuickRepliesContent(
                    state = state,
                    onBack = { navigator.navigateUp() }
                ) { event ->
                    viewModel.handleEvents(event)
                }
            }
        )
    }
}

@Composable
private fun QuickRepliesContent(
    state: QuickRepliesViewState,
    onBack: () -> Unit = {},
    event: (QuickRepliesViewEvent) -> Unit
) {
    val isLoading = state.screenState is QuickRepliesViewState.ScreenState.Loading
    val shimmer = rememberShimmer(ShimmerBounds.Window)
    val listState = rememberLazyListState()
    var isRefreshing by remember { mutableStateOf(false) }
    val pullToRefreshState = rememberPullToRefreshState(isRefreshing = isRefreshing)
    LaunchedEffect(isLoading) { if (!isLoading) isRefreshing = false }

    var showAddDialog by remember { mutableStateOf(false) }
    var showEditDialog by remember { mutableStateOf<QuickReply?>(null) }

    Box(
        modifier = Modifier.fillMaxSize().safeDrawingPadding()
    ) {
        Column(modifier = Modifier.sizeIn()) {
            Spacer(modifier = Modifier.size(8.dp))
            ContentHeader(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                text = stringResource(Res.string.quick_replies),
                leadingContent = {
                    UIIconBackButton(
                        onClick = {
                            onBack()
                        }
                    )
                }
            )
            UITextInput(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                label = stringResource(Res.string.search),
                borderWidth = 1.dp,
                leadingIcon = FeatherIcons.Search,
                trailingIcon = FeatherIcons.X
            ) { search ->
                event(QuickRepliesViewEvent.SearchQuickReply(search))
            }
            Spacer(modifier = Modifier.size(12.dp))

            PullToRefresh(
                state = pullToRefreshState,
                onRefresh = { isRefreshing = true; event(QuickRepliesViewEvent.Reload) },
                modifier = Modifier.fillMaxSize()
            ) {
                if (isLoading && state.quickReplies.isEmpty()) {
                    Column(modifier = Modifier.widthIn().padding(horizontal = 12.dp, vertical = 0.dp)) {
                        Spacer(modifier = Modifier.height(16.dp))
                        repeat(8) { QuickReplyItemSkeleton(shimmer) }
                    }
                } else if (!isLoading && state.quickReplies.isEmpty()) {
                    UIEmptyListState(text = stringResource(Res.string.empty_quick_replies))
                } else {
                    val listModifier = if (isLoading)
                        Modifier.widthIn().padding(horizontal = 12.dp).shimmer(shimmer)
                    else
                        Modifier.widthIn().padding(horizontal = 12.dp)
                    PaginatedLazyColumn(
                        modifier = listModifier,
                        loadMoreItems = { event(QuickRepliesViewEvent.LoadMore) },
                        listState = listState,
                        buffer = 5,
                        isLoading = isLoading,
                        paginatedTextError = (state.screenState as? QuickRepliesViewState.ScreenState.Error)?.text,
                        retryOnPaginated = { event(QuickRepliesViewEvent.LoadMore) }
                    ) {
                        item { Spacer(modifier = Modifier.height(16.dp)) }
                        items(state.quickReplies, key = { it.id }) { item ->
                            QuickReplyItem(item = item) { showEditDialog = item }
                        }
                    }
                }
            }
        }
        FloatingActionButton(
            modifier = Modifier
                .navigationBarsPadding()
                .padding(bottom = 32.dp, start = 24.dp, end = 24.dp, top = 16.dp)
                .align(Alignment.BottomEnd),
            shape = RoundedCornerShape(50),
            containerColor = DefaultTheme.colors.primary,
            contentColor = DefaultTheme.colors.onPrimary,
            onClick = { showAddDialog = true }
        ) {
            Icon(imageVector = FeatherIcons.Plus, contentDescription = null)
        }
    }

    if (showAddDialog) {
        AddQuickReplyDialog(
            onAdd = { event(QuickRepliesViewEvent.Reload) },
            onDismiss = { showAddDialog = false }
        )
    }

    showEditDialog?.let { quickReply ->
        EditQuickReplyDialog(
            quickReply = quickReply,
            onEdit = { event(QuickRepliesViewEvent.Reload) },
            onDelete = { id -> event(QuickRepliesViewEvent.DeleteQuickReply(id)) },
            onDismiss = { showEditDialog = null }
        )
    }
}

@Composable
private fun QuickReplyItemSkeleton(shimmer: kz.dentalcrm.app.theme.shimmer.Shimmer) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = DefaultTheme.colors.primarySubdued,
                shape = RoundedCornerShape(16.dp)
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Spacer(modifier = Modifier.height(12.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.8f)
                    .height(14.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.3f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(12.dp))
        }
        Spacer(modifier = Modifier.width(32.dp))
    }
    Spacer(Modifier.height(12.dp))
}

@Composable
fun QuickReplyItem(
    item: QuickReply,
    onClick: () -> Unit,
) {
    ContentButton(
        modifier = Modifier,
        shape = RoundedCornerShape(16.dp),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn()
                .background(
                    color = DefaultTheme.colors.primarySubdued,
                    shape = RoundedCornerShape(16.dp)
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Spacer(modifier = Modifier.width(12.dp))
            Column(modifier = Modifier.weight(1f)) {
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = item.message ?: "",
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    color = DefaultTheme.colors.onSurface,
                    style = DefaultTheme.typography.bodyMediumMedium,
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "ID: ${item.id}",
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 1,
                    color = DefaultTheme.colors.onSurfaceVariant,
                    style = DefaultTheme.typography.bodySmallRegular,
                )
                Spacer(modifier = Modifier.height(12.dp))
            }
            Spacer(modifier = Modifier.width(8.dp))
            Icon(
                imageVector = chevronRight,
                tint = DefaultTheme.colors.onSurface,
                contentDescription = null
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
    }
    Spacer(Modifier.height(12.dp))
}
